'use client'

import { useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Search, X } from 'lucide-react'
import { navigateUrl } from '@/utils/client-request'
import BrandCard from './BrandCard'

const firstLetter = (s: string) => (s?.trim()?.charAt(0) || '').toUpperCase()

export default function BrandList({ brands, vendor }: { brands: any, vendor?: any }) {
  const pathname = usePathname() || ''
  const [query, setQuery] = useState('')
  const list: any[] = brands || []
  const total = list.length
  const vendorName = vendor?.name || 'Shop.mn'

  const filtered = useMemo(() => {
    if (!query.trim()) return list
    const q = query.trim().toLowerCase()
    return list.filter(b => String(b?.name || '').toLowerCase().includes(q))
  }, [list, query])

  const groups = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => {
      const an = String(a.name || '').toLowerCase()
      const bn = String(b.name || '').toLowerCase()
      return an < bn ? -1 : an > bn ? 1 : 0
    })
    const map = new Map<string, any[]>()
    for (const b of sorted) {
      const L = firstLetter(b.name)
      if (!map.has(L)) map.set(L, [])
      map.get(L)!.push(b)
    }
    return [...map.entries()]
  }, [filtered])

  const reset = () => setQuery('')

  return (
    <section className="bg-cream pt-6 pb-16">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <nav className="text-[13px] text-foreground/55 mb-6">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Бүх брэндүүд</span>
        </nav>

        <div className="relative rounded-3xl overflow-hidden bg-foreground text-white px-6 sm:px-12 py-10 sm:py-14">
          <div className="relative max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/70">Бүх дэлгүүрүүд</div>
            <h1 className="mt-3 text-3xl sm:text-5xl font-bold font-gilroy leading-[1.05] tracking-[-0.02em]">
              <span style={{ background: 'linear-gradient(90deg,#7DD3FC 0%,#C7D2FE 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                {total.toLocaleString('en-US')} брэнд
              </span>
              <span className="block text-white">нэг дороос хайж олоорой</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/80 max-w-lg leading-7">
              {vendorName}-ийн брэндүүдээс дуртайгаа сонгон бүх бүтээгдэхүүнийг нэг дороос үзээрэй.
            </p>
            <div className="mt-7 relative max-w-xl">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/55"><Search className="size-[18px]" /></div>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Брэнд эсвэл дэлгүүрийн нэрээр хайх…"
                className="w-full h-14 pl-12 pr-4 rounded-2xl bg-white text-foreground placeholder:text-foreground/55 text-[15px] shadow-lg border-0 outline-none focus:ring-4 focus:ring-white/30"
              />
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-foreground/15 bg-[#F1EEE7]/40 py-16 px-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-white border border-foreground/10 inline-flex items-center justify-center mb-4">
              <Search className="size-[22px] text-foreground/55" />
            </div>
            <div className="text-base font-semibold text-foreground">Брэнд олдсонгүй</div>
            <div className="mt-1.5 text-[14px] text-foreground/55">Хайлтын утга эсвэл шүүлтүүрээ өөрчилж үзнэ үү.</div>
            {query && (
              <button onClick={reset} className="mt-5 h-9 px-4 inline-flex items-center gap-1.5 rounded-full bg-white border border-foreground/10 text-[13px] font-medium hover:border-foreground/30 transition-colors cursor-pointer">
                <X className="size-3.5" /> Хайлт цэвэрлэх
              </button>
            )}
          </div>
        ) : (
          <div className="mt-10 space-y-12">
            {groups.map(([letter, items]) => (
              <div key={letter}>
                <div className="flex items-center gap-4 mb-5">
                  <h3 className="text-3xl font-bold font-gilroy tracking-[-0.02em] text-foreground">{letter}</h3>
                  <div className="flex-1 h-px bg-foreground/10" />
                  <span className="text-[12px] text-foreground/55">{items.length} брэнд</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8">
                  {items.map((b: any, i: number) => (
                    <BrandCard key={b.id} brand={b} index={i} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </section>
  )
}
